// Globální boční panel Hlídače — pool + Team Season DNA analýza odkudkoli.
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Siren, X, ArrowsClockwise, ArrowSquareOut, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSharedPool } from "../lib/timing-pool-store";
import { timingPoolAnalyze } from "../lib/timing-pool-api";
import { t } from "../lib/timing-i18n";

const PHASES = [
    { key: "gw1_6", label: "GW1-6" }, { key: "gw7_12", label: "GW7-12" },
    { key: "gw13_18", label: "GW13-18" }, { key: "gw19_24", label: "GW19-24" },
    { key: "gw25_30", label: "GW25-30" }, { key: "gw31_38", label: "GW31-38" },
];
const CACHE_KEY = "fa.dna.drawer.cache";

function poolHash(pool) {
    return pool.map((p) => p.fpl_id).sort((a, b) => a - b).join(",");
}

function readCache(hash) {
    try {
        const c = JSON.parse(sessionStorage.getItem(CACHE_KEY));
        if (c && c.hash === hash) return c.data;
    } catch { /* ignore */ }
    return null;
}

export function useDnaAnalysis(pool, enabled) {
    const [analysis, setAnalysis] = useState(null);
    const [busy, setBusy] = useState(false);
    const hash = useMemo(() => poolHash(pool), [pool]);

    const analyze = async (force = false) => {
        if (pool.length < 5) return;
        if (!force) {
            const cached = readCache(hash);
            if (cached) { setAnalysis(cached); return; }
        }
        setBusy(true);
        try {
            const d = await timingPoolAnalyze(pool.map((p) => ({
                fpl_id: p.fpl_id, historical_slug: p.historical_slug,
                display_name: p.display_name, position: p.position, team: p.team,
                current_price: p.current_price, ownership_pct: p.ownership_pct,
                availability_pct: 100,
            })));
            setAnalysis(d);
            try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ hash, data: d })); } catch { /* ignore */ }
        } catch (e) { toast.error(e?.response?.data?.detail || "Analýza selhala"); }
        finally { setBusy(false); }
    };

    useEffect(() => {
        if (enabled) analyze();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, hash]);

    return { analysis, busy, analyze };
}

export function DnaAnalysisBody({ pool, analysis, busy }) {
    const topDna = useMemo(() => {
        if (!analysis?.players) return [];
        return [...analysis.players]
            .filter((p) => p.scores?.season_dna != null)
            .sort((a, b) => b.scores.season_dna - a.scores.season_dna)
            .slice(0, 5);
    }, [analysis]);
    const dna = analysis?.dna;

    return (
        <>
            {pool.length < 5 && (
                <div className="border border-white/10 bg-black/40 p-4 font-mono text-[11px] text-white/60 space-y-2" data-testid="dna-drawer-empty">
                    <p>Pro Team Season DNA analýzu potřebuješ aspoň 5 hlídaných hráčů (teď {pool.length}).</p>
                    <p className="text-white/40">Hráče přidáš tlačítkem „Do Hlídače" na profilu hráče nebo ve finderech.</p>
                </div>
            )}

            {busy && !analysis && (
                <div className="border border-white/10 bg-black/40 p-4 font-mono text-[11px] text-white/50 flex items-center gap-2">
                    <ArrowsClockwise size={13} className="animate-spin" /> Hlídač analyzuje pool…
                </div>
            )}

            {dna && (
                <>
                    <div className="border border-fa-yellow/30 bg-fa-yellow/5 p-4 flex items-center gap-4" data-testid="dna-drawer-score">
                        <div>
                            <div className="font-mono text-[8px] uppercase tracking-widest text-white/45">DNA skóre</div>
                            <div className="font-display font-black text-4xl text-fa-yellow tabular-nums leading-none mt-1">
                                {dna.average_dna?.toFixed(1) ?? "—"}
                            </div>
                            <div className="font-mono text-[8px] text-white/35">/100</div>
                        </div>
                        <div className="flex flex-wrap gap-1 flex-1">
                            {(dna.typology || []).map((k) => (
                                <span key={k} className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-fa-sky/40 bg-fa-sky/5 text-fa-sky">
                                    {t(`typo_${k}`, "cs")}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="border border-white/10 bg-[#0d0d0d] p-3 space-y-1.5" data-testid="dna-drawer-phases">
                        <div className="font-mono text-[8px] uppercase tracking-widest text-white/45 mb-1">Síla ve fázích sezóny</div>
                        {PHASES.map((ph) => {
                            const v = dna.phase_strengths?.[ph.key] ?? 0;
                            return (
                                <div key={ph.key} className="flex items-center gap-2">
                                    <span className="font-mono text-[9px] text-white/50 w-14 tabular-nums">{ph.label}</span>
                                    <div className="flex-1 h-2 bg-white/5">
                                        <div className={`h-full ${v >= 60 ? "bg-fa-mint" : v >= 40 ? "bg-fa-sky" : "bg-[#FF8800]"}`}
                                             style={{ width: `${Math.min(v, 100)}%` }} />
                                    </div>
                                    <span className="font-mono text-[9px] text-white/60 w-7 text-right tabular-nums">{Math.round(v)}</span>
                                </div>
                            );
                        })}
                    </div>

                    {topDna.length > 0 && (
                        <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="dna-drawer-top">
                            <div className="font-mono text-[8px] uppercase tracking-widest text-white/45 mb-1.5">Top Season DNA</div>
                            <ol className="space-y-1">
                                {topDna.map((p, i) => (
                                    <li key={p.player_key || p.fpl_id} className="flex items-center gap-2 font-mono text-[11px]">
                                        <span className="text-white/40 w-3 tabular-nums">{i + 1}</span>
                                        <span className="flex-1 font-display font-bold text-sm truncate">{p.display_name}</span>
                                        <span className="text-white/40 tabular-nums">{p.current_price == null ? "£—" : `£${p.current_price.toFixed(1)}`}</span>
                                        <span className="text-fa-yellow font-bold tabular-nums w-8 text-right">{p.scores.season_dna.toFixed(0)}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {analysis.risks?.length > 0 && (
                        <div className="border border-[#FF8800]/30 bg-[#FF8800]/5 p-3 font-mono text-[10px] text-white/70 space-y-1" data-testid="dna-drawer-risks">
                            <div className="flex items-center gap-1.5 text-[#FF8800] uppercase tracking-widest text-[8px]">
                                <Warning size={11} weight="fill" /> Hlídač varuje ({analysis.risks.length})
                            </div>
                            {analysis.risks.slice(0, 3).map((r, i) => (
                                <p key={i}>
                                    <span className="text-red-200 font-bold">{r.player?.display_name}</span>
                                    <span className="text-white/50 ml-1.5">{(r.risks || []).join(", ")}</span>
                                </p>
                            ))}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default function SeasonDnaDrawer() {
    const { pool, totalPrice } = useSharedPool();
    const [open, setOpen] = useState(false);
    const { analysis, busy, analyze } = useDnaAnalysis(pool, open);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    return (
        <>
            <button type="button" onClick={() => setOpen(true)} title="Hlídač"
                    data-testid="dna-drawer-btn"
                    className="relative p-2 border border-transparent text-white/60 hover:text-[#FF8800] hover:border-[#FF8800]/40 transition-colors">
                <Siren size={17} weight="bold" />
                {pool.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 font-mono text-[8px] bg-[#FF8800] text-black px-1 font-bold tabular-nums">
                        {pool.length}
                    </span>
                )}
            </button>

            {open && createPortal(
                <div className="fixed inset-0 z-50" data-testid="dna-drawer">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="dna-drawer-backdrop" />
                    <aside className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-[#0b0817] border-l border-[#FF8800]/25 overflow-y-auto animate-in slide-in-from-right duration-200">
                        <div className="sticky top-0 bg-[#0b0817]/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-2 z-10">
                            <Siren size={15} weight="bold" className="text-[#FF8800]" />
                            <div className="font-display font-black uppercase tracking-tight text-sm">Hlídač <span className="text-white/40 font-mono text-[9px] tracking-widest">· Season DNA</span></div>
                            <span className="font-mono text-[9px] text-white/40 tabular-nums">{pool.length} hráčů · £{totalPrice.toFixed(1)}m</span>
                            <button type="button" onClick={() => analyze(true)} disabled={busy || pool.length < 5} title="Přepočítat"
                                    data-testid="dna-drawer-refresh"
                                    className="ml-auto p-1.5 text-white/50 hover:text-[#FF8800] disabled:opacity-30">
                                <ArrowsClockwise size={13} className={busy ? "animate-spin" : ""} />
                            </button>
                            <button type="button" onClick={() => setOpen(false)} data-testid="dna-drawer-close"
                                    className="p-1.5 text-white/50 hover:text-white">
                                <X size={15} />
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            <DnaAnalysisBody pool={pool} analysis={analysis} busy={busy} />
                            <Link to="/hlidac" onClick={() => setOpen(false)} data-testid="dna-drawer-full-link"
                                  className="flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest px-4 py-2.5 border border-[#FF8800]/50 text-[#FF8800] hover:bg-[#FF8800] hover:text-black transition-colors">
                                <ArrowSquareOut size={12} /> Otevřít Hlídače
                            </Link>
                        </div>
                    </aside>
                </div>,
                document.body
            )}
        </>
    );
}
